import { Loader2, SearchX } from "lucide-react";
import type { ReactNode } from "react";
import {
  CustomerErrorViewer,
  useCustomerErrorDialog,
} from "@/components/customer-error-viewer";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";

export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn("size-4 animate-spin", className)} aria-hidden="true" />;
}

export function ErrorState({
  error,
  onRetry,
  message,
}: {
  error?: unknown;
  onRetry?: () => void;
  message?: string;
}) {
  const { t } = useI18n();
  useCustomerErrorDialog(
    error,
    onRetry ? { actionLabel: t("common.retry"), onAction: onRetry } : undefined,
  );
  return (
    <CustomerErrorViewer
      error={error}
      message={message}
      actionLabel={onRetry ? t("common.retry") : undefined}
      onAction={onRetry}
      className="surface-card"
    />
  );
}

export function EmptyState({
  title,
  description,
  icon,
  action,
}: {
  title: string;
  description?: string | undefined;
  icon?: ReactNode | undefined;
  action?: ReactNode | undefined;
}) {
  return (
    <div className="surface-card pattern-levant flex flex-col items-center gap-3 px-6 py-14 text-center">
      <span className="grid size-16 place-items-center rounded-full bg-card text-primary shadow-soft">
        {icon ?? <SearchX className="size-7" aria-hidden="true" />}
      </span>
      <h3 className="text-lg font-bold">{title}</h3>
      {description ? <p className="max-w-sm text-sm text-muted-foreground">{description}</p> : null}
      {action}
    </div>
  );
}

export function Shimmer({ className }: { className?: string }) {
  return <div className={cn("animate-pulse rounded-xl bg-muted", className)} />;
}

export function ProductSkeletonList({ count = 4 }: { count?: number }) {
  return (
    <div className="grid gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="surface-card flex gap-4 p-4">
          <Shimmer className="size-28 shrink-0 rounded-2xl" />
          <div className="flex flex-1 flex-col gap-3 py-1">
            <Shimmer className="h-5 w-1/3" />
            <Shimmer className="h-4 w-2/3" />
            <Shimmer className="h-4 w-1/4" />
            <Shimmer className="mt-auto h-9 w-32 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function CardSkeletonGrid({ count = 6 }: { count?: number }) {
  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="surface-card overflow-hidden">
          <Shimmer className="h-44 w-full rounded-none" />
          <div className="flex flex-col gap-3 p-5">
            <Shimmer className="h-5 w-2/3" />
            <Shimmer className="h-4 w-full" />
            <Shimmer className="h-9 w-28 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}
